/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { type MenuItem } from '@dm/ang';
import { type Command, type PromptConfigFull, LazyloadService, LoadingService, PromptService } from '@dm/core';
import { SelectOption } from '@dm/core/types';
import { MO } from 'app/common/constants';
import { ScheduleTypeE } from 'app/common/constants/hyper-cdp.const';
import { type FileSystem, type HyperCdpSchedule, type Lun, type ProtectGroup, type ReservedShotNum } from 'app/common/types/mo';
import { ShardSwitch, SWITCH } from 'app/config/switch';
import { tap } from 'rxjs/operators';

import { ScheduleService } from '../schedule.service';
import { ScheduleStrService } from '../schedule-str.service';
import { ConfigPolicyComponent } from './config-policy/config-policy.component';
import { ConfigPolicySComponent } from './config-policy/config-policy-s/config-policy-s.component';
import { ConfirmComponent } from './confirm/confirm.component';
import { CreateStrService, objectTypeE } from './create-str.service';
import { SpecifyObjComponent } from './specify-obj/specify-obj.component';

@Component({
    selector: 'app-hyper-cdp-schedule-create',
    templateUrl: './create.component.html',
    styleUrls: ['./create.component.scss'],
})
export class CreateComponent implements OnInit {
    @Output() onClose = new EventEmitter();
    @Input() type: ScheduleTypeE = ScheduleTypeE.Block;
    @Input() vstore: SelectOption;
    @ViewChild('specificObjDt') specificStep: SpecifyObjComponent;
    @ViewChild('configPolicyDt') configPolicyStep: ConfigPolicyComponent;
    @ViewChild('confrimDt') confirmStep: ConfirmComponent;
    @ViewChild('configPolicySDt') configPolicySDt: ConfigPolicySComponent;

    ScheduleTypeE = ScheduleTypeE;
    windowVisible = true;
    i18n = this.str;

    steps: MenuItem[];
    activeIndex = -1;

    selectedPG: ProtectGroup[] = [];
    selectedLUN: Lun[] = [];
    selectedFS: FileSystem[] = [];
    selection: any = [];
    policyValue: any;
    createConfig: HyperCdpSchedule;
    createdObj: HyperCdpSchedule;
    isEn = this.str.isEn();
    cmdExcuting = false;
    cmds: Command[] = [];
    selectedVstore: SelectOption;

    isSimpleMode = SWITCH.IS_SIMPLE_MODE;

    ShardSwitch = ShardSwitch;

    constructor(
        private lazyloadService: LazyloadService,
        private str: CreateStrService,
        private scheduleStr: ScheduleStrService,
        private service: ScheduleService,
        private promptService: PromptService,
        private loadingService: LoadingService,
    ) { }

    ngOnInit(): void {
        this.activeIndex = 0;
        this.windowVisible = true;
        this.initSteps();
    }

    initSteps(): void {
        this.steps = [
            {
                label: this.str.title.specifyObj,
                command: (): void => {
                    this.activeIndex = 0;
                },
            },
            {
                label: this.str.title.configPolicy,
                command: (): void => {
                    this.activeIndex = 1;
                },
            },
            {
                label: this.str.title.confirm,
                command: (): void => {
                    this.activeIndex = 2;
                },
            },
        ];
    }

    previous(): void {
        this.activeIndex--;
    }

    next(): void {
        // 当前页校验通过，才能到下一页
        if (this.activeIndex === 0) {
            this.specificStep.vm.validate((valid) => {
                if (!valid) {
                    return;
                }

                this.selection = this.specificStep.getSelection();
                this.selectedPG = this.specificStep.getSelectedPG();
                this.selectedLUN = this.specificStep.getSelectedLUN();
                this.selectedFS = this.specificStep.getSelectedFS();
                this.selectedVstore = this.specificStep.selectedVstore;

                this.activeIndex++;
            });
        } else if (this.activeIndex === 1) {
            // 极简模式
            if (this.isSimpleMode) {
                if (!this.configPolicySDt.executionCycleValidate()) {
                    return;
                }

                this.createConfig = this.getScheduleConfig();
                this.activeIndex++;

                return;
            }

            // 标准模式
            if (!this.configPolicyStep.validate()) {
                return;
            }
            this.createConfig = this.getScheduleConfig();
            this.policyValue = this.configPolicyStep.vm.getRawValue();
            this.configPolicyStep.vm
                .validate((valid) => {
                    if (!valid) {
                        return;
                    }
                    this.activeIndex++;
                });
        }
    }

    getScheduleConfig(): HyperCdpSchedule {
        const basicValue = this.specificStep.vm.value;
        const scheduleValue = this.isSimpleMode ?
            this.configPolicySDt.getPolicyConfig() : this.configPolicyStep.getPolicyConfig();

        const data: HyperCdpSchedule = {
            NAME: basicValue.name,
            ...scheduleValue,
        };
        data.OBJECTTYPE = this.type === ScheduleTypeE.Block ? objectTypeE.block : objectTypeE.file;
        if (basicValue.description) {
            data.DESCRIPTION = basicValue.description;
        }

        if (data.SECURESNAPENABLED) {
            delete data.FREQUENCYNUM;
            delete data.DAILYSNAPSHOTNUM;
            delete data.WEEKLYSNAPSHOTNUM;
            delete data.MONTHSNAPSHOTNUM;
        }

        return data;
    }

    confrim(): void {
        // 极简模式场景，因为3个步骤表单都是放在一起显示的，这里连续触发一下第一步和第二步的表单校验，校验不通过直接return
        if (this.isSimpleMode) {
            this.activeIndex = 0;
            this.next();
            this.next();

            if (this.activeIndex !== 2) {
                return;
            }
        }

        const prompts: PromptConfigFull[] = [];
        if (this.createConfig.SECURESNAPENABLED) {
            prompts.push({
                id: 'prompt-cdpSchedule-createHighRisk',
                title: this.i18n.title.createTitle,
                content: this.str.create.createHighRisk,
                width: 600,
                opType: 'warning',
            });
        } else if (this.createConfig.FREQUENCYSECURESNAPENABLED ||
            this.createConfig.DAYSECURESNAPENABLED ||
            this.createConfig.WEEKLYSECURESNAPENABLED ||
            this.createConfig.MONTHSECURESNAPENABLED) {
            prompts.push({
                id: 'prompt-cdpSchedule-createFsHighRisk',
                title: this.i18n.title.createTitle,
                content: this.str.create.createFsHighRisk,
                width: 600,
                opType: 'warning',
            });
        }

        this.loadingService.show();
        this.service.getScheduleReservedNum().subscribe(({ data }: { data: ReservedShotNum }) => {
            const reservedNum = Number(data.COUNT);
            const isNeedlimitedNumPrompts = this.isSimpleMode ?
                this.configPolicySDt.exceedTheLimitNum(reservedNum) : this.configPolicyStep.exceedTheLimitNum(reservedNum);

            if (isNeedlimitedNumPrompts) {
                prompts.push({
                    title: this.i18n.title.createTitle,
                    content: this.str.create.limitedNumHighRisk,
                    width: 600,
                    opType: 'danger',
                });
            }

            this.loadingService.hide();
            showPrompts();
        }, () => {
            this.loadingService.hide();
            showPrompts();
        });

        const showPrompts = (): void => {
            if (prompts.length) {
                this.promptService.multi(prompts, () => {
                    this.promptService.close();
                    this.doCreateHyperCDPSchedule();
                });
            } else {
                this.doCreateHyperCDPSchedule();
            }
        };
    }

    doCreateHyperCDPSchedule(): void {
        this.cmds = [];
        this.cmdExcuting = true;

        const createHyperCDPScheduleCmd: Command = {
            id: 'createHyperCDPSchedule',
            name: this.str.createResult(this.createConfig.NAME),
            action: this.service.create(
                this.createConfig,
                this.specificStep.selectedVstore.value,
                { doException: false },
            )
                .pipe(
                    tap(({ data }) => this.createdObj = data),
                ),
        };
        this.cmds.push(createHyperCDPScheduleCmd);

        this.selection.forEach((item, index) => {
            let name;
            let cmd: Command;
            if (item.TYPE === MO.LUN) {
                name = this.scheduleStr.addMemberLunMsg(item.NAME, this.createConfig.NAME);
                cmd = {
                    id: 'addMember' + index,
                    deps: ['createHyperCDPSchedule'],
                    name,
                    action: ({ data }) => this.service.addAssociateMember(
                        data.ID,
                        item.ID,
                        item.TYPE,
                        { doException: false },
                        item.vstoreId,
                    ),
                };
            } else if (item.TYPE === MO.PROTECT_GROUP) {
                const params: any = {
                    protectGroupId: item.protectGroupId,
                };
                cmd = {
                    id: 'addMember' + index,
                    deps: ['createHyperCDPSchedule'],
                    name: this.scheduleStr.addMemberPgMsg(item.protectGroupName),
                    action: ({ data }) => this.service.addPG2Schedule(data.NAME,
                        { ...params, cdpScheduleId: data.ID }, {
                        showResult: false,
                        frontendDisplayResult: false,
                        customDetail: this.i18n.executeResult_taskDetail_info,
                    }),
                };
            } else {
                name = this.scheduleStr.addMemberFsMsg(item.NAME, this.createConfig.NAME);
                cmd = {
                    id: 'addMember' + index,
                    deps: ['createHyperCDPSchedule'],
                    name,
                    action: ({ data }) => this.service.addAssociateMember(
                        data.ID,
                        item.ID,
                        item.TYPE,
                        { doException: false },
                        item.vstoreId,
                    ),
                };
            }

            this.cmds.push(cmd);
        });

        if (this.confirmStep.isActive) {
            const cmd: Command = {
                id: 'activate',
                deps: ['createHyperCDPSchedule'],
                name: this.str.title.activateMsg.replace('{0}', this.createConfig.NAME),
                action: ({ data }) => this.service.activate(
                    data.ID,
                    this.type === this.ScheduleTypeE.File ? this.specificStep.selectedVstore.value : undefined,
                    { doException: false },
                ),
            };
            this.cmds.push(cmd);
        }
        this.activeIndex = 3;
    }

    /**
     * 命令执行完毕
     */
    onComplete(): void {
        this.cmdExcuting = false;
    }

    close(): void {
        this.onClose.emit(this.createdObj);
        this.lazyloadService.destroySelf(this);
    }
}
